import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import { Alert, Button, TextField } from '@decisiv/ui-components';
import { t, Trans } from '@lingui/macro';
import Loading from '@decisiv/ui-components/lib/components/Loading';

import TopNav from '@decisiv/ui-components/lib/components/TopNav';
import Paper from '../components/Paper';
import Helmet from '../components/SEO/Helmet';
import AvatarProd from '../images/logo.png';
import AvatarStaging from '../images/AvatarStaging.png';
import AvatarQa from '../images/AvatarQA.png';
import { Divider } from '../components/StyledComponents';

import '../styles/SignIn.css';
import LoginApi from '../api/login';
import { encryptStorage } from '../utils/storage';
import { notifyBetaFeaturesChanged } from '../hooks/useBetaFeatures';
import oktaLogo from '../images/okta_logo.png';
import getEnvironment from '../utils/getCurrentEnv';

export default function SignIn() {
  const navigate = useNavigate();
  const passwordRef = useRef(null);
  const userNameRef = useRef(null);
  const [showAlert, setShowAlert] = useState(false);
  const [isOktaLogin, setIsOktaLogin] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const showUsernamePasswordAccessInLoginPage =
    process.env.REACT_APP_SHOW_USERNAME_PASSWORD_ACCESS_IN_LOGIN_PAGE ===
    'true';

  useEffect(() => {
    const handleOktaLogin = async (token) => {
      setIsButtonDisabled(true);
      setShowAlert(false);
      setIsOktaLogin(true);

      const queryParams = new URLSearchParams(window.location.search);
      const redirectError = queryParams.get('error');
      if (redirectError) {
        setErrorMessage(redirectError);
        setShowAlert(true);
        return;
      }

      const response = await LoginApi.loginRequest('', '', token);

      if (response.errors) {
        setErrorMessage(response.errors[0].detail);
        setShowAlert(true);
        setIsButtonDisabled(false);
        return;
      }

      encryptStorage.setItem(
        'authToken',
        response.data.attributes.access_token,
      );
      encryptStorage.setItem(
        'userSession',
        JSON.stringify(response.data.attributes),
      );
      encryptStorage.setItem('lastLogin', new Date().toISOString());
      notifyBetaFeaturesChanged();

      navigate('/integrations');
    };

    // Okta redirect login. Keyed on the callback params rather than on the
    // query being non-empty: any unauthenticated deep link keeps its query
    // through the redirect here, and `?foo=bar` must still show the form.
    const queryParams = new URLSearchParams(window.location.search);
    const ssoToken = queryParams.get('sso_token');
    if (ssoToken || queryParams.get('error')) {
      handleOktaLogin(ssoToken);
    }

    const keyDownHandler = (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        document.getElementById('login').click();
      }
    };
    document.addEventListener('keydown', keyDownHandler);
    return () => {
      document.removeEventListener('keydown', keyDownHandler);
    };
  }, []);

  const handleClick = async () => {
    setIsButtonDisabled(true);
    setShowAlert(false);

    const response = await LoginApi.loginRequest(
      userNameRef.current.value,
      passwordRef.current.value,
    );

    if (response.errors) {
      setErrorMessage(response.errors[0].detail);
      setShowAlert(true);
      setIsButtonDisabled(false);
      return;
    }

    encryptStorage.setItem('authToken', response.data.attributes.access_token);
    encryptStorage.setItem(
      'userSession',
      JSON.stringify(response.data.attributes),
    );
    encryptStorage.setItem('lastLogin', new Date().toISOString());
    notifyBetaFeaturesChanged();

    navigate('/integrations');
  };

  const currentEnv = getEnvironment(process.env.REACT_APP_BASE_URL);

  const getImageByEnv = () => {
    const environment = currentEnv || 'qa';

    const images = {
      qa: AvatarQa,
      staging: AvatarStaging,
      'decisiv-paccar-escrow-20': AvatarStaging,
      prod: AvatarProd,
    };

    return images[environment] || images.qa;
  };

  return (
    <>
      <Helmet title={t`Log in to Integration Framework`} />
      <Paper css="height: 100%;" padding={0}>
        <TopNav className="TopNav">
          <Flex paddingX={1}>
            <TopNav.BrandInfo
              logo={getImageByEnv()}
              primaryText="Decisiv"
              secondaryText="Integration Framework"
            />
          </Flex>
        </TopNav>

        <Flex
          alignItems="center"
          flexDirection="column"
          className="PageContainer"
        >
          <form name="loginForm">
            <Flex
              alignItems="center"
              flexDirection="column"
              className="SignInForm"
            >
              <span className="LoginHeaderText">
                <Trans>Log in to Integration Framework</Trans>
                {isOktaLogin && (
                  <Flex marginTop={1} justifyContent="center">
                    <Flex>
                      <img
                        className="if-invert-on-dark"
                        style={{ width: '300px', height: '100px' }}
                        src={oktaLogo}
                        alt="Okta Logo"
                      />
                    </Flex>
                  </Flex>
                )}

                {!showAlert && isOktaLogin && (
                  <Loading size="large" description="Logging in" />
                )}
              </span>
              <Flex
                alignItems="center"
                flexDirection="column"
                className="SignInFields"
              >
                {showAlert && (
                  <Flex className="WideFlex">
                    <Flex flex={1}>
                      <Trans>
                        <Alert title={errorMessage} intent="danger" />
                      </Trans>
                    </Flex>
                  </Flex>
                )}

                {isOktaLogin ? (
                  <Flex className="WideFlex">
                    <a href="/sign_in">Return to login page</a>
                  </Flex>
                ) : (
                  <>
                    {showUsernamePasswordAccessInLoginPage && (
                      <>
                        <Flex className="WideFlex">
                          <Flex flex={1}>
                            <Trans>
                              <TextField
                                ref={userNameRef}
                                label="Username or email"
                                name="LoginInformation"
                              />
                            </Trans>
                          </Flex>
                        </Flex>
                        <Flex className="WideFlex">
                          <Flex flex={1}>
                            <Trans>
                              <TextField
                                ref={passwordRef}
                                label="Password"
                                name="password"
                                type="password"
                              />
                            </Trans>
                          </Flex>
                        </Flex>
                        <Flex className="WideFlex">
                          <Trans>
                            <Button
                              type="button"
                              id="login"
                              text="LOG IN"
                              kind="primary"
                              onClick={handleClick}
                              disabled={isButtonDisabled}
                            />
                          </Trans>
                        </Flex>

                        <Flex className="WideFlex">
                          <Divider className="LeftDivider" />
                          <span>
                            <Trans>Or</Trans>
                          </span>
                          <Divider className="RightDivider" />
                        </Flex>
                      </>
                    )}
                    <Flex className="WideFlex">
                      <a href={process.env.REACT_APP_OKTA_AUTH_URL}>
                        Log in with Single Sign On (SSO)
                      </a>
                    </Flex>
                  </>
                )}
              </Flex>
            </Flex>
          </form>
          <Flex alignItems="center" flexDirection="column">
            <p>
              <span className="LicenseAndAgreements">
                <Trans>By logging into the application you agree to the</Trans>
              </span>{' '}
              <span className="LicenseLink LicenseAndAgreements">
                <Trans>Policies & Agreements.</Trans>
              </span>
            </p>
          </Flex>
        </Flex>
      </Paper>
    </>
  );
}
